// Persistance d'un « enregistrement » de suivi en direct.
//
// Un enregistrement = une fenêtre de temps ouverte par l'animateur (« le groupe
// arrive, je lance le suivi »). Toute session du parcours démarrée après
// `startedAt` y est rattachée, et le reste ensuite définitivement, même
// terminée : c'est ce qui permet de garder le tableau complet en fin de partie.
//
// Pourquoi mémoriser les ids en plus de la date de début : l'API pagine les
// sessions. Une session rattachée puis sortie de la page interrogée (parcours
// très fréquenté) disparaîtrait de l'écran. Les ids gardent la liste stable, et
// survivent à un rechargement de page.

const STORAGE_PREFIX = "lz.liveRun.";

const storageKey = (tourId) => `${STORAGE_PREFIX}${tourId}`;

// Le localStorage peut être indisponible (navigation privée, quota, iframe
// cloisonnée). Le suivi doit continuer à fonctionner sans persistance plutôt
// que de faire tomber la page.
const safeRead = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

export const createRun = (tourId) => ({
  tourId,
  startedAt: new Date().toISOString(),
  closedAt: null,
  sessionIds: [],
});

export const loadRun = (tourId) => {
  const raw = safeRead(storageKey(tourId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.startedAt) return null;
    return {
      tourId,
      startedAt: parsed.startedAt,
      closedAt: parsed.closedAt || null,
      sessionIds: Array.isArray(parsed.sessionIds) ? parsed.sessionIds : [],
    };
  } catch (error) {
    return null;
  }
};

export const saveRun = (run) => {
  if (!run?.tourId) return;
  safeWrite(storageKey(run.tourId), JSON.stringify(run));
};

export const clearRun = (tourId) => {
  try {
    window.localStorage.removeItem(storageKey(tourId));
  } catch (error) {
    /* rien à faire : l'enregistrement n'était pas persisté */
  }
};

// Rattache à l'enregistrement les sessions démarrées après son ouverture.
// Renvoie `null` si rien n'a changé, pour éviter un re-render inutile à chaque
// tour de polling.
export const attachNewSessions = (run, sessions) => {
  if (!run) return null;

  const startedAtMs = new Date(run.startedAt).getTime();
  // Un enregistrement clôturé n'accroche plus de nouvelles sessions, mais
  // continue d'afficher les siennes.
  const closedAtMs = run.closedAt ? new Date(run.closedAt).getTime() : null;
  const known = new Set(run.sessionIds);

  const added = sessions
    .filter((session) => {
      if (known.has(session.id)) return false;
      const createdAtMs = new Date(session.created_at).getTime();
      if (Number.isNaN(createdAtMs) || createdAtMs < startedAtMs) return false;
      return closedAtMs === null || createdAtMs <= closedAtMs;
    })
    .map((session) => session.id);

  if (added.length === 0) return null;

  return { ...run, sessionIds: [...run.sessionIds, ...added] };
};
